/**
 * What the slice is, what it hides, and the one control that ends it (`KLQ-20260820-005` §2).
 *
 * The register has said *"Showing tasks under Block A, Level 3"* since `KLQ-20260821-001`, and
 * that sentence had two holes. It named the selection as one comma-joined string, so a person
 * holding two subtrees could drop neither without dropping both; and it never said what was
 * *not* on screen, so a slice somebody had forgotten looked exactly like a project with very
 * little work in it. An empty-looking register is the failure this feature exists to prevent,
 * and the slicer was reproducing it one level down.
 *
 * So: one chip per selected node, each chip removing itself, the number of reachable tasks the
 * slice leaves out, and *Show the whole project*. The number comes from `excluded_by_slice` on
 * the envelope of the list the view **already mounted** — this component issues no request and
 * takes no query of its own, which is why it takes the count as a prop rather than fetching it.
 *
 * **The vocabulary is looking, never permission** (spec §2). No padlock, no *restricted*, no
 * *you may not*: a slice is a question this person asked and can un-ask, and it lives inside
 * the zone scope rather than beside it. `under=` narrows within what the caller may reach and
 * can never widen it, so clearing it restores their own project-wide register and nothing more.
 * Sharing a visual with the People screen's scope picker would say the opposite.
 */

import { X } from "lucide-react";

import { useMessages } from "@/lib/i18n";

import styles from "./Tasks.module.css";

export interface SliceBarProps {
  /** The selected subtree ids, in the order the rail holds them. */
  readonly under: readonly string[];
  /** The hierarchy's name for a node, or `null` when this client cannot name it. */
  readonly nameOf: (id: string) => string | null;
  /**
   * Reachable tasks the slice left out, off the list's envelope. `null` when the view has no
   * page yet or the server sent no count — absent, and never rendered as a zero, because
   * *"hiding 0"* and *"we do not know yet"* are different sentences.
   */
  readonly excluded: number | null;
  /** The whole next selection. Empty is *show everything*. */
  readonly onChange: (under: readonly string[]) => void;
}

export function SliceBar({ under, nameOf, excluded, onChange }: SliceBarProps) {
  const t = useMessages("tasks");

  if (under.length === 0) {
    return null;
  }

  return (
    <div className={styles.sliceBar}>
      <span className={styles.subtree}>{t("under.showing")}</span>
      <ul className={styles.sliceChips}>
        {under.map((id) => {
          const name = nameOf(id) ?? t("under.unknown");
          return (
            <li key={id}>
              <button
                type="button"
                className={styles.sliceChip}
                aria-label={t("under.remove", { name })}
                onClick={() => {
                  // The whole next selection, computed here and applied in one navigation —
                  // the rail's own contract. Removing the last chip is `[]`, which is the
                  // same thing *Show the whole project* does and needs no special case.
                  onChange(under.filter((other) => other !== id));
                }}
              >
                <span>{name}</span>
                <X aria-hidden size={12} />
              </button>
            </li>
          );
        })}
      </ul>
      {excluded === null || excluded === 0 ? null : (
        <span className={styles.sliceHidden}>
          {t("under.hidden", { count: excluded })}
        </span>
      )}
      <button
        type="button"
        className={styles.linkButton}
        onClick={() => {
          // Only `under`. Every other predicate on this screen belongs to the filter bar and
          // is cleared by it — a control that quietly cleared both would make *Show the whole
          // project* undo a filter somebody set deliberately.
          onChange([]);
        }}
      >
        {t("under.clear")}
      </button>
    </div>
  );
}
